// src/pages/admin/AdminDashboard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import api from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import AdminActionGroup from '../components/AdminActionGroup';
import { buildSessionTrend } from '../utils/sessionTrend';

const pct = (num) => (Number.isFinite(num) ? `${(num * 100).toFixed(1)}%` : '-');
const safeDiv = (a, b) => (b ? a / b : 0);
const lastTwo = (arr = []) => (arr.length >= 2 ? arr.slice(-2) : [arr[arr.length - 1] || null, null]);

const valueGradientSx = {
  backgroundImage: 'linear-gradient(90deg, var(--color-primary-800), var(--color-primary-600))',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  fontWeight: 800,
};

const NeutralChip = ({ children }) => (
  <Chip
    size="small"
    label={children}
    variant="outlined"
    sx={{
      bgcolor: 'grey.50',
      borderColor: 'grey.200',
      color: 'text.secondary',
      fontWeight: 700,
      letterSpacing: '0.2px',
    }}
  />
);

const DeltaChip = ({ diff, base, neutralText }) => {
  if (!Number.isFinite(diff) || !Number.isFinite(base) || base === 0) {
    return <NeutralChip>{neutralText ?? '-'}</NeutralChip>;
  }
  if (diff === 0) {
    return <NeutralChip>No change</NeutralChip>;
  }
  const up = diff >= 0;
  const ratio = Math.abs(diff / base);
  return (
    <Chip
      size="small"
      icon={up ? <TrendingUpRoundedIcon fontSize="small" /> : <TrendingDownRoundedIcon fontSize="small" />}
      label={`${pct(ratio)} vs prev`}
      sx={{
        bgcolor: up ? 'success.light' : 'error.light',
        color: up ? 'success.dark' : 'error.dark',
        '& .MuiChip-icon': { color: 'inherit' },
        fontWeight: 700,
        letterSpacing: '0.2px',
      }}
    />
  );
};

const SectionCard = ({ title, subtitle, action, children, contentSx, sx, ...props }) => (
  <Card sx={{ height: '100%', ...sx }} {...props}>
    {(title || subtitle || action) && (
      <CardHeader
        title={
          title ? (
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
              {title}
            </Typography>
          ) : undefined
        }
        subheader={
          subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : undefined
        }
        action={action}
        sx={{
          alignItems: 'flex-start',
          '& .MuiCardHeader-action': {
            mt: { xs: 2, md: 0 },
            alignSelf: { xs: 'stretch', md: 'center' },
            width: 'auto',
          },
          pb: children ? 0 : 2,
        }}
      />
    )}
    {children ? (
      <CardContent
        sx={{
          pt: title || subtitle || action ? 2 : 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          ...contentSx,
        }}
      >
        {children}
      </CardContent>
    ) : null}
  </Card>
);

const StatCard = ({ label, value, adornment, helperText, icon, iconColor = 'primary' }) => {
  const displayValue = Number.isFinite(value) ? value.toLocaleString() : value;
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack spacing={0.75}>
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.main',
                letterSpacing: '1.1px',
                fontWeight: 800,
              }}
            >
              {label}
            </Typography>
            <Typography component="p" variant="h4" sx={valueGradientSx}>
              {displayValue}
            </Typography>
          </Stack>
          {(icon || adornment) && (
            <Stack spacing={1} alignItems="flex-end">
              {icon && (
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: (theme) => theme.palette[iconColor]?.light || theme.palette.primary.light,
                    color: (theme) => theme.palette[iconColor]?.dark || theme.palette.primary.dark,
                    width: 44,
                    height: 44,
                  }}
                >
                  {icon}
                </Avatar>
              )}
              {adornment}
            </Stack>
          )}
        </Stack>
        {helperText && (
          <Typography variant="body2" color="text.secondary">
            {helperText}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const subjectColors = ['primary', 'success', 'secondary', 'warning', 'info'];

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRange, setSelectedRange] = useState('7d');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [data, setData] = useState({
    studentCount: 0,
    tutorCount: 0,
    parentCount: 0,
    studentCountAll: 0,
    tutorCountAll: 0,
    parentCountAll: 0,
    totalSessions: 0,
    completedSessions: 0,
    upcomingSessions: 0,
    popularSubjects: [],
    studentCountHistory: [],
    tutorCountHistory: [],
    parentCountHistory: [],
    completedSessionsHistory: [],
    upcomingSessionsHistory: [],
  });
  const timerRef = useRef(null);

  const loadSummary = useCallback(
    async (rangeOverride) => {
      const range = rangeOverride || selectedRange;
      setLoading(true);
      setError(null);
      try {
        const { data: payload } = await api.get('/analytics/admin-summary', { params: { range } });
        setData(payload);
        setLastUpdated(new Date());
      } catch (err) {
        setError(err?.response?.data?.message || err.message || 'Failed to load analytics.');
      } finally {
        setLoading(false);
      }
    },
    [selectedRange]
  );

  useEffect(() => {
    loadSummary(selectedRange);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => loadSummary(selectedRange), 60_000);
    return () => clearInterval(timerRef.current);
  }, [selectedRange, loadSummary]);

  const {
    studentCount,
    tutorCount,
    parentCount,
    totalSessions,
    completedSessions,
    upcomingSessions,
    popularSubjects,
    studentCountHistory,
    tutorCountHistory,
    parentCountHistory,
    completedSessionsHistory,
    upcomingSessionsHistory,
    tutorCountAll,
    parentCountAll,
    studentCountPrev,
    tutorCountPrev,
    parentCountPrev,
    completedSessionsPrev,
  } = data;

  const completionRate = useMemo(() => safeDiv(completedSessions, totalSessions), [completedSessions, totalSessions]);
  const sessionsPerTutor = useMemo(
    () => safeDiv(totalSessions, tutorCountAll || tutorCount),
    [totalSessions, tutorCountAll, tutorCount]
  );

  const [, studPrev] = useMemo(
    () => lastTwo(studentCountHistory.map((d) => d.value ?? d.count ?? 0)),
    [studentCountHistory]
  );
  const [, tutPrev] = useMemo(
    () => lastTwo(tutorCountHistory.map((d) => d.value ?? d.count ?? 0)),
    [tutorCountHistory]
  );
  const [, parentPrev] = useMemo(
    () => lastTwo(parentCountHistory.map((d) => d.value ?? d.count ?? 0)),
    [parentCountHistory]
  );
  const [, compPrev] = useMemo(
    () => lastTwo(completedSessionsHistory.map((d) => d.value ?? d.count ?? 0)),
    [completedSessionsHistory]
  );

  const rangeLabels = useMemo(
    () => ({
      '24h': '24 hours',
      '7d': '7 days',
      '30d': '30 days',
      '90d': '90 days',
      all: 'all time',
    }),
    []
  );
  const isAllTime = selectedRange === 'all';
  const rangeLabel = rangeLabels[selectedRange] || 'selected range';
  const rangeWindowText = isAllTime ? 'all time' : `last ${rangeLabel}`;

  const upcomingToday = useMemo(() => {
    const today = new Date();
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    const todayKey = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    return (upcomingSessionsHistory || [])
      .filter((d) => String(d.date || '').startsWith(todayKey))
      .reduce((sum, row) => sum + (row?.value ?? row?.count ?? 0), 0);
  }, [upcomingSessionsHistory]);

  const studentPrevBase =
    selectedRange === 'all'
      ? null
      : Number.isFinite(studentCountPrev)
      ? studentCountPrev
      : studPrev ?? null;
  const tutorPrevBase =
    selectedRange === 'all'
      ? null
      : Number.isFinite(tutorCountPrev)
      ? tutorCountPrev
      : tutPrev ?? null;
  const parentPrevBase =
    selectedRange === 'all'
      ? null
      : Number.isFinite(parentCountPrev)
      ? parentCountPrev
      : parentPrev ?? null;
  const completedPrevBase =
    selectedRange === 'all'
      ? null
      : Number.isFinite(completedSessionsPrev)
      ? completedSessionsPrev
      : compPrev ?? null;

  const subjectsTotal = useMemo(
    () => popularSubjects.reduce((total, subject) => total + (subject?.count ?? 0), 0),
    [popularSubjects]
  );

  const overviewAction = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'stretch', sm: 'center' }}
    >
      <ToggleButtonGroup
        size="small"
        value={selectedRange}
        exclusive
        onChange={(_, value) => value && setSelectedRange(value)}
        sx={{ flexWrap: 'wrap' }}
      >
        <ToggleButton value="24h">24h</ToggleButton>
        <ToggleButton value="7d">7d</ToggleButton>
        <ToggleButton value="30d">30d</ToggleButton>
        <ToggleButton value="90d">90d</ToggleButton>
        <ToggleButton value="all">All time</ToggleButton>
      </ToggleButtonGroup>
      <Tooltip title="Refresh now">
        <span>
          <Button
            variant="contained"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => loadSummary(selectedRange)}
            disabled={loading}
            sx={{ minWidth: 140 }}
          >
            Refresh
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );

  const quickActions = (
    <SectionCard
      title="Quick Actions"
      subtitle="Jump straight to common admin workflows."
      action={
        <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Last refreshed
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {lastUpdated ? lastUpdated.toLocaleString() : '-'}
          </Typography>
        </Stack>
      }
    >
      <AdminActionGroup>
        <Button
          variant="outlined"
          startIcon={<GroupRoundedIcon />}
          href="/admin/users"
          sx={{ minWidth: 160 }}
        >
          Manage Users
        </Button>
        <Button
          variant="outlined"
          startIcon={<CampaignRoundedIcon />}
          href="/admin/announcements"
          sx={{ minWidth: 180 }}
        >
          Announcements
        </Button>
      </AdminActionGroup>
    </SectionCard>
  );

  const keyMetricsSection = loading ? (
    <SectionCard title="Key Metrics">
      <Grid container spacing={2.5}>
        {Array.from({ length: 5 }).map((_, idx) => (
          <Grid key={idx} item xs={12} sm={6} md={2.4}>
            <Skeleton variant="rounded" height={160} />
          </Grid>
        ))}
      </Grid>
    </SectionCard>
  ) : (
    <SectionCard
      title="Key Metrics"
      subtitle={`High-level counters for ${rangeWindowText}.`}
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            label="New Students"
            value={studentCount}
            adornment={
              <DeltaChip
                diff={Number.isFinite(studentPrevBase) ? studentCount - (studentPrevBase ?? 0) : NaN}
                base={studentPrevBase ?? NaN}
                neutralText={selectedRange === 'all' ? 'All time' : 'No prior period'}
              />
            }
            helperText={isAllTime ? 'All time' : `Created in the last ${rangeLabel}`}
            icon={<SchoolRoundedIcon fontSize="small" />}
            iconColor="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            label="New Tutors"
            value={tutorCount}
            adornment={
              <DeltaChip
                diff={Number.isFinite(tutorPrevBase) ? tutorCount - (tutorPrevBase ?? 0) : NaN}
                base={tutorPrevBase ?? NaN}
                neutralText={selectedRange === 'all' ? 'All time' : 'No prior period'}
              />
            }
            helperText={isAllTime ? 'All time' : `Created in the last ${rangeLabel}`}
            icon={<PersonSearchRoundedIcon fontSize="small" />}
            iconColor="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Sessions in Range"
            value={totalSessions}
            adornment={
              <NeutralChip>
                {Number.isFinite(sessionsPerTutor) ? sessionsPerTutor.toFixed(1) : '-'} / tutor
              </NeutralChip>
            }
            helperText={isAllTime ? 'All time' : `Sessions scheduled in the last ${rangeLabel}`}
            icon={<VideocamRoundedIcon fontSize="small" />}
            iconColor="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            label="New Parents"
            value={parentCount}
            adornment={
              <DeltaChip
                diff={Number.isFinite(parentPrevBase) ? parentCount - (parentPrevBase ?? 0) : NaN}
                base={parentPrevBase ?? NaN}
                neutralText={selectedRange === 'all' ? 'All time' : 'No prior period'}
              />
            }
            helperText={isAllTime ? `${parentCountAll.toLocaleString()} all-time parents` : `Created in the last ${rangeLabel}`}
            icon={<GroupsRoundedIcon fontSize="small" />}
            iconColor="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            label="Completed in Range"
            value={completedSessions}
            adornment={
              <DeltaChip
                diff={Number.isFinite(completedPrevBase) ? completedSessions - (completedPrevBase ?? 0) : NaN}
                base={completedPrevBase ?? NaN}
                neutralText={selectedRange === 'all' ? 'All time' : 'No prior period'}
              />
            }
            helperText={isAllTime ? 'All time' : `Completed in the last ${rangeLabel}`}
            icon={<TaskAltRoundedIcon fontSize="small" />}
            iconColor="success"
          />
        </Grid>
      </Grid>
    </SectionCard>
  );

  const subjectsSection = loading ? (
    <SectionCard title="Top Subjects">
      <Stack spacing={1.5}>
        {Array.from({ length: 4 }).map((_, idx) => (
          <Skeleton key={idx} variant="rounded" height={56} />
        ))}
      </Stack>
    </SectionCard>
  ) : (
    <SectionCard
      title="Top Subjects"
      subtitle={`Most attended subjects for ${rangeWindowText}.`}
    >
      {popularSubjects.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No subject data yet.
        </Typography>
      ) : (
        <List disablePadding>
          {popularSubjects.map((subject, index) => {
            const count = subject?.count ?? 0;
            const share = subjectsTotal ? Math.round((count / subjectsTotal) * 100) : 0;
            return (
              <ListItem
                key={`${subject.subject || 'unknown'}-${index}`}
                disableGutters
                sx={{ px: 0 }}
                secondaryAction={
                  <Stack spacing={0.5} alignItems="flex-end" sx={{ minWidth: 120 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {count.toLocaleString()}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={share}
                      sx={{ width: 110, borderRadius: 999 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {share}% share
                    </Typography>
                  </Stack>
                }
              >
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    sx={{
                      bgcolor: (theme) => theme.palette[subjectColors[index % subjectColors.length]]?.light || theme.palette.primary.light,
                      color: (theme) => theme.palette[subjectColors[index % subjectColors.length]]?.dark || theme.palette.primary.dark,
                      fontWeight: 700,
                    }}
                  >
                    {(subject.subject || '-').slice(0, 1).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={subject.subject || '-'}
                  secondary={`${subject.program ? `${subject.program.toUpperCase()} - ` : ''}${share}% of top subjects`}
                  primaryTypographyProps={{ fontWeight: 600, color: 'text.primary' }}
                  secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </SectionCard>
  );

  const sessionTrend = useMemo(() => {
    return buildSessionTrend(completedSessionsHistory, upcomingSessionsHistory);
  }, [completedSessionsHistory, upcomingSessionsHistory]);

  const operationalSnapshot = (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(300px, 1fr)' }, alignItems: 'start', gap: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <SectionCard title="Sessions This Week" subtitle="Scheduled and completed lesson activity" contentSx={{ gap: 2.5 }} sx={{ height: 'auto' }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
            <Typography variant="caption" color="text.secondary"><Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mr: 0.75 }} />Scheduled</Typography>
            <Typography variant="caption" color="text.secondary"><Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.main', mr: 0.75 }} />Completed</Typography>
          </Stack>
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={{ xs: 0.5, sm: 1 }} sx={{ height: { xs: 190, sm: 205 }, px: { xs: 0, sm: 1 }, overflow: 'hidden' }}>
            {sessionTrend.map((point) => (
              <Stack key={point.label} alignItems="center" justifyContent="flex-end" spacing={0.75} sx={{ flex: '1 1 0', minWidth: 0, height: '100%' }}>
                <Stack direction="row" alignItems="flex-end" justifyContent="center" spacing={{ xs: 0.25, sm: 0.5 }} sx={{ height: { xs: 150, sm: 165 }, width: '100%', minWidth: 0 }}>
                  <Box sx={{ flexShrink: 0, width: { xs: 7, sm: 14 }, maxHeight: '100%', height: `${Math.min(100, Math.max(8, (point.scheduled / point.max) * 100))}%`, bgcolor: 'primary.main', borderRadius: '5px 5px 2px 2px', opacity: 0.85 }} />
                  <Box sx={{ flexShrink: 0, width: { xs: 7, sm: 14 }, maxHeight: '100%', height: `${Math.min(100, Math.max(5, (point.completed / point.max) * 100))}%`, bgcolor: 'secondary.main', borderRadius: '5px 5px 2px 2px' }} />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, flexShrink: 0 }}>{point.label}</Typography>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      </Box>
      <Box>
        <Stack spacing={2} sx={{ height: '100%' }}>
          <SectionCard title="Completion Rate" contentSx={{ gap: 0.5, flex: 1 }}>
            <Typography component="p" variant="h3" sx={{ color: completionRate < 0.5 ? 'warning.main' : 'secondary.main', fontWeight: 900 }}>{pct(completionRate)}</Typography>
            <Typography variant="body2" color="text.secondary">{completedSessions.toLocaleString()} of {totalSessions.toLocaleString()} sessions completed</Typography>
          </SectionCard>
          <SectionCard title="Upcoming Sessions" contentSx={{ gap: 0.5, flex: 1 }}>
            <Typography component="p" variant="h3" sx={{ color: 'primary.main', fontWeight: 900 }}>{upcomingSessions.toLocaleString()}</Typography>
            <Typography variant="body2" color="text.secondary">{upcomingToday} scheduled today</Typography>
          </SectionCard>
          <SectionCard title="Average Tutor Load" contentSx={{ gap: 0.5, flex: 1 }}>
            <Typography component="p" variant="h3" sx={{ color: 'primary.main', fontWeight: 900 }}>{Number.isFinite(sessionsPerTutor) ? sessionsPerTutor.toFixed(1) : '-'}</Typography>
            <Typography variant="body2" color="text.secondary">Across {(tutorCountAll || tutorCount).toLocaleString()} active tutors</Typography>
          </SectionCard>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <SectionCard
          action={overviewAction}
          contentSx={{ pt: 0 }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Chip
              icon={<RefreshRoundedIcon fontSize="small" />}
              label={`Last updated ${lastUpdated ? lastUpdated.toLocaleString() : 'never'}`}
              size="small"
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              Data auto-refreshes every minute. Adjust the range to explore trends.
            </Typography>
          </Stack>
        </SectionCard>

        {error ? (
          <Alert severity="error" sx={{ borderRadius: 18 }}>
            {error}
          </Alert>
        ) : (
          <>
            {keyMetricsSection}
            {operationalSnapshot}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(360px, .85fr)' }, gap: 2 }}>
              <Box>{quickActions}</Box>
              <Box>{subjectsSection}</Box>
            </Box>
          </>
        )}
      </Stack>
    </DashboardLayout>
  );
};

export default AdminDashboard;
